import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/app/store/authStore';
import { useQuery } from '@tanstack/react-query';
import { taskService } from '@/app/services/taskService';
import { listParties } from '@/app/services/partyService';
import { CheckCircle2, Clock, Users, Plus, ListTodo } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useTaskStore } from '@/app/store/taskStore';
import { CreateTaskModal } from '@/app/pages/tasks/components/CreateTaskModal';
import DashboardTaskPanels from '@/app/components/tasks/DashboardTaskPanels';

/**
 * Client Manager Dashboard Page
 *
 * Overview dashboard for client_manager role users featuring:
 * - Task statistics (open, completed)
 * - Assigned parties count
 * - Quick actions to create tasks and view parties
 * - Recent tasks preview
 */
const ClientManagerDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { openCreateModal } = useTaskStore();

  // Fetch task counts
  const { data: taskCounts, isLoading: isLoadingTaskCounts } = useQuery({
    queryKey: ['dashboardTaskCounts'],
    queryFn: async () => {
      const [openTasks, completedTasks] = await Promise.all([
        taskService.list({ status: 'open' as any, limit: 1, skip: 0 }),
        taskService.list({ status: 'completed' as any, limit: 1, skip: 0 }),
      ]);

      return {
        open: openTasks.total || 0,
        completed: completedTasks.total || 0,
        total: (openTasks.total || 0) + (completedTasks.total || 0),
      };
    },
  });

  // Fetch assigned parties count (for client managers, this will be filtered by backend)
  const { data: partiesData, isLoading: isLoadingParties } = useQuery({
    queryKey: ['dashboardParties'],
    queryFn: () => listParties({ limit: 1, skip: 0 }),
  });

  // Fetch recent tasks
  const { data: recentTasks, isLoading: isLoadingRecentTasks } = useQuery({
    queryKey: ['recentTasks'],
    queryFn: () => taskService.list({ limit: 5, skip: 0 }),
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Welcome back, {user?.full_name || user?.username}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Open Tasks */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Tasks</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoadingTaskCounts ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-xl font-semibold tracking-tight">{taskCounts?.open || 0}</div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Tasks pending completion</p>
          </CardContent>
        </Card>

        {/* Completed Tasks */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed Tasks</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoadingTaskCounts ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-xl font-semibold tracking-tight">{taskCounts?.completed || 0}</div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Tasks completed</p>
          </CardContent>
        </Card>

        {/* My Parties */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">My Parties</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoadingParties ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-xl font-semibold tracking-tight">{partiesData?.total || 0}</div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Assigned customers</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks and shortcuts</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <Button onClick={openCreateModal} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Task
          </Button>
          <Button variant="outline" onClick={() => navigate('/client-manager/tasks')} className="gap-2">
            <ListTodo className="h-4 w-4" />
            View All Tasks
          </Button>
          <Button variant="outline" onClick={() => navigate('/client-manager/parties?group=Client')} className="gap-2">
            <Users className="h-4 w-4" />
            View Clients
          </Button>
        </CardContent>
      </Card>

      {/* My / Team task panels — operationally actionable */}
      <DashboardTaskPanels />

      {/* Recent Tasks */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Tasks</CardTitle>
              <CardDescription>Your latest tasks</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/client-manager/tasks')}>
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingRecentTasks ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : recentTasks && recentTasks.items.length > 0 ? (
            <div className="space-y-4">
              {recentTasks.items.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent cursor-pointer"
                  onClick={() => navigate(`/tasks/detail/${task.id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{task.title}</p>
                    {task.description && (
                      <p className="text-sm text-muted-foreground truncate">{task.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        task.status === 'open'
                          ? 'bg-primary/10 text-primary'
                          : 'bg-success/10 text-success'
                      }`}
                    >
                      {task.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>No recent tasks</p>
              <Button variant="link" onClick={openCreateModal} className="mt-2">
                Create your first task
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Task Modal */}
      <CreateTaskModal />
    </div>
  );
};

export default ClientManagerDashboardPage;
